import { IconButton, Tooltip } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';

import type {
  SidebarCardId,
  SidebarSectionId,
} from 'components/conversation/components/sidebar/conversationSidebar.types';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { useCardSectionVisibility } from '../../hooks/useCardSectionVisibility';
import { useFlatCardContext } from '../../hooks/useFlatCardContext/useFlatCardContext';
import { useSidebarMode } from '../../hooks/useSidebarMode';

type VisibilityToggleProps = {
  itemId: SidebarCardId | SidebarSectionId;
  itemType: 'section' | 'card';
  /**
   * Only render on the flat path (when a {@link FlatCardContext} is present).
   * Used by cards that were never hideable in the legacy sidebar but should be
   * hideable in the admin-managed flat model.
   */
  flatOnly?: boolean;
};

export const VisibilityToggle = ({
  itemId,
  itemType,
  flatOnly,
}: VisibilityToggleProps): JSX.Element | null => {
  const flatCard = useFlatCardContext();
  const { hiddenItems, toggleItemVisibility } = useCardSectionVisibility();
  const flags = useFeatureFlag(['conversation-right-sidebar-visibility']);

  if (useSidebarMode() !== 'customization') {
    return null;
  }

  // Cards that opt into `flatOnly` keep their legacy behavior (not hideable)
  // when rendered outside the flat model.
  if (flatOnly && !flatCard) {
    return null;
  }

  // On the flat path visibility comes from the flat model; the context's
  // presence implies the feature is on. The legacy path uses the old model +
  // flag.
  if (!flatCard && !flags.conversationRightSidebarVisibility) {
    return null;
  }

  // On the flat path the toggle reflects the agent's own choice (ignoring
  // conditions) so a condition-hidden card can still be toggled; the legacy path
  // uses the section-visibility set.
  const isHidden = flatCard ? flatCard.isUserHidden : hiddenItems.has(itemId);

  const tooltipContent =
    flatCard?.isConditionHidden && !isHidden
      ? `Conditionally hidden. ${itemType === 'card' ? 'Card' : 'Section'} still shows when the conditions match.`
      : isHidden
        ? `Show ${itemType}`
        : `Hide ${itemType}`;

  return (
    <Tooltip content={tooltipContent}>
      <IconButton
        icon={isHidden ? 'eye-slash' : 'eye'}
        variant="tertiary"
        onClick={(event) => {
          event.stopPropagation();
          if (flatCard) {
            flatCard.toggleVisibility();
          } else {
            toggleItemVisibility(itemId);
          }

          ampli.conversationRightSidebarItemVisibilityToggled({
            isVisible: isHidden,
            sidebarItemType: itemType,
          });
        }}
      />
    </Tooltip>
  );
};
